import { ActionReducerMapBuilder, createAction, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import isArray from 'lodash/isArray'
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'

import { UpdateDrawableMeasurements } from '../sagas/2D/updateDrawableCoordinates'
import { NormalizedDocumentChunk, selectActiveDocumentChunkIds } from './documents'
import {
    ACTIVATE_ONLY_CLICKED_DRAWABLE_GROUP_BY_TYPE,
    DELETE_OPENING_GROUPS,
    FETCH_OPENING_GROUPS_SUCCESS,
    SET_ACTIVE_DRAWABLE_GROUP,
    TOGGLE_ACTIVE_DRAWABLE,
    TOGGLE_DRAWABLE_GROUP,
    TOGGLE_DRAWABLE_GROUPS_BY_TYPE,
    TOGGLE_DRAWABLE_GROUPS_VISIBILITY,
    UPDATE_DRAWABLES_MEASUREMENTS,
    UPDATE_JOIST_LINES,
    UPDATE_OPENING_GROUPS_SUCCESS,
} from '../../actions/drawable'
import { UpdateOpeningGroupApiResponse } from '../../api/api-helper'
import { Opening, Opening_location, OpeningAPI, OpeningGroupAPI } from '../../models/activeDrawable'
import { DRAWABLE_TYPES } from '../../shared/constants/drawable-types'
import IndexableObject from '../../shared/constants/general-enums/indexableObject'
import { PROJECT_PAGE_TAB_VALUES } from '../../shared/constants/project-settings-names'
import { RootState } from '../../stores'
import { hasGeometryInRegion } from '../../utils/coordinates/defineLocationRegion'

/** TYPES AND INTERFACES */

export type Geometry = Omit<Opening, 'id' | 'additional_data' | 'opening_group_id' | 'outputs'> &
    Omit<Opening_location, 'document_chunk'> & {
        geometryGroupId: number
        openingAdditionalData: Record<string, any>
        isActive?: boolean
        openingSettings: Record<string, any>
    }

export type GeometryActiveDictionary = Record<number, Record<number, boolean>>

export type GeometryVisibilityRecord = Record<number, boolean>

export type GeometricDrawable = Omit<OpeningAPI, 'outputs'> & { isActive?: boolean; isVisible: boolean }

export type GeometryGroupWithTempType = GeometryGroup & { tempType?: string }

export type ActiveGeometryGroup = Omit<OpeningGroupAPI, 'openings'> & {
    drawables: GeometricDrawable[]
    openings: GeometricDrawable[]
}

export type GeometryGroup = Omit<OpeningGroupAPI, 'openings'> & {
    drawablesPerTab: GeometricDrawable[]
    openings: GeometricDrawable[]
    isActive: Record<number, boolean>
}

export type GeometryState = {
    activeGeometryGroupID: number | null
    geometryGroupActiveRecord: GeometryActiveDictionary
    geometries: Geometry[]
    allGroupIds: number[]
    groupToCreate: ActiveGeometryGroup | null
    geometryVisibilityRecord: GeometryVisibilityRecord
    lastActiveGroupType: DRAWABLE_TYPES | null
}

export const initialGeometryState: GeometryState = {
    activeGeometryGroupID: null,
    geometryGroupActiveRecord: {},
    geometries: [],
    allGroupIds: [],
    groupToCreate: null,
    geometryVisibilityRecord: {},
    lastActiveGroupType: null,
}

/** OLD STORE ACTIONS TO HOOK INTO */

export const gotOpeningGroups = createAction<OpeningGroupAPI[]>(FETCH_OPENING_GROUPS_SUCCESS)
export const setActiveGroup = createAction<{ activeDrawableGroup: any | []; activeDrawableId?: number | number[] }>(
    SET_ACTIVE_DRAWABLE_GROUP
)
export const updateGeometries = createAction<{ openingGroups: UpdateOpeningGroupApiResponse }>(
    UPDATE_OPENING_GROUPS_SUCCESS
)

export const deleteGeometries = createAction<{ openingGroupsToDelete: OpeningGroupAPI[] }>(DELETE_OPENING_GROUPS)

export const updateGeometryMeasurements = createAction<UpdateDrawableMeasurements>(UPDATE_DRAWABLES_MEASUREMENTS)
export const toggleGeometryGroup = createAction<{ drawables: GeometryGroup | []; activeChunkId: number | undefined }>(
    TOGGLE_DRAWABLE_GROUP
)
export const toggleGeometryGroupByType = createAction<{ type: GeometryGroup[]; activeChunkId: number | undefined }>(
    TOGGLE_DRAWABLE_GROUPS_BY_TYPE
)
export const activateSingleGeometryGroupByType = createAction<{
    type: GeometryGroup[]
    activeChunkId: number | undefined
}>(ACTIVATE_ONLY_CLICKED_DRAWABLE_GROUP_BY_TYPE)
export const resetGeometryGroupVisibility = createAction<{ isShowAllTabs: boolean }>(TOGGLE_DRAWABLE_GROUPS_VISIBILITY)
export const toggleActiveGeometries = createAction<{ drawable_id: number[] | number }>(TOGGLE_ACTIVE_DRAWABLE)
export const updateGeometryJoistLines = createAction<{ res: OpeningGroupAPI }>(UPDATE_JOIST_LINES)

/** UTILITY FUNCTIONS */

const convertOpeningsAndLocationsToGeometries = (openingGroups: OpeningGroupAPI[]): Geometry[] => {
    return openingGroups.flatMap((openingGroup) =>
        openingGroup.openings.flatMap((opening) => {
            return opening.opening_locations.map((loc) => {
                const newGeometry: Geometry = {
                    location: loc.location,
                    document_chunk_id: loc.document_chunk_id,
                    coordinates: loc.coordinates,
                    cutouts: loc.cutouts,
                    additional_data: loc.additional_data,
                    src: loc.src,
                    floor_hash: loc.floor_hash,
                    id: loc.id,
                    opening_id: loc.opening_id,
                    region_id: loc.region_id,
                    three_d_identifier: loc.three_d_identifier,
                    project_id: opening.project_id,
                    openingAdditionalData: opening.additional_data,
                    geometryGroupId: openingGroup.id,
                    label: opening.label,
                    order: opening.order,
                    settings: openingGroup.settings,
                    remarks: openingGroup.remarks,
                    configuration: openingGroup.configuration,
                    color: openingGroup.color?.length === 0 ? null : openingGroup.color,
                    type: openingGroup.type,
                    openingSettings: opening.settings,
                    ai_suggestion_id: opening.ai_suggestion_id,
                }

                return newGeometry
            })
        })
    )
}

const convertGeometriesToGeometricDrawable = (
    documentChunks: NormalizedDocumentChunk[] | null,
    geometries: Geometry[],
    geometryVisibilityRecord: GeometryVisibilityRecord
): { drawables: GeometricDrawable[]; groupSettings: Record<string, any> } => {
    if (isNull(documentChunks)) return { drawables: [], groupSettings: {} }

    let groupSettings = {}
    const groupedOpenings: Record<number, number> = {}

    const geometriesHash = {}

    for (const geometry of geometries) {
        if (!(geometry.opening_id in geometriesHash)) {
            geometriesHash[geometry.opening_id] = []
        }
        geometriesHash[geometry.opening_id].push(geometry)
    }

    const geometricDrawables = geometries.reduce((drawables, geometry) => {
        if (!groupedOpenings[geometry.opening_id]) {
            const geometriesInOpening = geometriesHash[geometry.opening_id]

            groupSettings = geometriesInOpening[0].settings
            const isUndefinedActive = geometriesInOpening.some((geo) => isUndefined(geo.isActive))

            const geoDrawable: GeometricDrawable = {
                id: geometry.opening_id,
                additional_data: geometry.openingAdditionalData,
                type: geometry.type,
                remarks: geometry.remarks,
                color: geometry.color,
                configuration: geometry.configuration,
                floor_hash: geometry.floor_hash,
                project_id: geometry.project_id,
                label: geometry.label,
                order: geometry.order,
                settings: geometry.openingSettings as any,
                opening_group_id: geometry.geometryGroupId,
                isVisible: geometryVisibilityRecord?.[geometry.opening_id] ?? true,
                opening_locations: geometriesInOpening.map((geo) => {
                    const chunk = documentChunks.find((doc) => doc.id === geo.document_chunk_id)

                    return {
                        id: geo.id,
                        document_chunk_id: geo.document_chunk_id,
                        document_chunk: chunk
                            ? {
                                  id: chunk.id,
                                  page: chunk.page,
                                  project_document_id: chunk.project_document_id,
                                  src: chunk.src,
                              }
                            : null,
                        coordinates: geo.coordinates,
                        cutouts: geo.cutouts,
                        additional_data: geo.additional_data,
                        location: geo.location,
                        floor_hash: geo.floor_hash,
                        region_id: geo.region_id,
                        opening_id: geo.opening_id,
                        three_d_identifier: geo.three_d_identifier,
                    } as Opening_location
                }),
                ai_suggestion_id: geometry.ai_suggestion_id,
            }

            if (!isUndefinedActive) {
                geoDrawable.isActive = geometriesInOpening.every((geo) => geo.isActive === true)
            }

            drawables.push(geoDrawable)
            groupedOpenings[geometry.opening_id] = geometry.opening_id
        }

        return drawables
    }, [] as GeometricDrawable[])

    return { drawables: geometricDrawables, groupSettings: groupSettings }
}

/** OLD ACTIONS HANDLERS */

function handleGotOpeningGroups(state: GeometryState, { payload }: PayloadAction<OpeningGroupAPI[]>) {
    state.geometries = convertOpeningsAndLocationsToGeometries(
        payload.filter((group) => group.type !== DRAWABLE_TYPES.HIGHLIGHTER && group.type !== DRAWABLE_TYPES.NOTE)
    )
    if (Object.keys(state.geometryGroupActiveRecord).length === 0) {
        state.geometryGroupActiveRecord = state.geometries.reduce((acc, { geometryGroupId, document_chunk_id }) => {
            if (!acc[geometryGroupId]) {
                acc[geometryGroupId] = { [document_chunk_id]: true }
            } else {
                acc[geometryGroupId] = { ...acc[geometryGroupId], [document_chunk_id]: true }
            }

            return acc
        }, {})
    }
    state.allGroupIds = [...new Set(state.geometries.map((geo) => geo.geometryGroupId))]
}

function handleSetActiveGroup(
    state: GeometryState,
    { payload }: PayloadAction<{ activeDrawableGroup: any | []; activeDrawableId?: number | number[] }>
) {
    if (isArray(payload.activeDrawableGroup)) {
        state.activeGeometryGroupID = null
    } else {
        state.lastActiveGroupType = payload.activeDrawableGroup.type
        state.activeGeometryGroupID = payload.activeDrawableGroup.id

        state.geometries = state.geometries.map((geo) => {
            geo.isActive = payload.activeDrawableId
                ? geo.opening_id === payload.activeDrawableId ||
                  (isArray(payload.activeDrawableId) && payload.activeDrawableId.includes(geo.opening_id))
                : geo.geometryGroupId === payload.activeDrawableGroup.id

            return geo
        })
    }
}

/**
 * Extracts geometries from the original group that should remain after an opening moves to a different group.
 * This ensures the source group state is updated correctly in the left panel.
 * Returns empty array for same-group updates (deletions) or when there are no remaining openings.
 */
export function getOriginalGroupGeometriesForMove(openingGroups: UpdateOpeningGroupApiResponse): Geometry[] {
    const isSameGroup = openingGroups.originalGroup?.id === openingGroups.newGroup.id

    if (isSameGroup || !openingGroups.originalGroup) {
        return []
    }

    const newGroupOpeningIds = new Set(openingGroups.newGroup.openings.map((opening) => opening.id))
    const originalGroupWithoutMovedOpenings = {
        ...openingGroups.originalGroup,
        openings: openingGroups.originalGroup.openings.filter((opening) => !newGroupOpeningIds.has(opening.id)),
    }

    if (originalGroupWithoutMovedOpenings.openings.length === 0) {
        return []
    }

    return convertOpeningsAndLocationsToGeometries([originalGroupWithoutMovedOpenings])
}

function handleUpdateGeometries(
    state: GeometryState,
    { payload }: PayloadAction<{ openingGroups: UpdateOpeningGroupApiResponse }>
) {
    const openingIdsAffected: Set<number> = new Set()
    const geoOpeningIdsAlreadyAddedByNewGeometries: Record<string, boolean> = {}

    // Opening Locations Geometries (new)
    const newGeometries: Geometry[] = convertOpeningsAndLocationsToGeometries([payload.openingGroups.newGroup])

    // Identify which openings are truly new (not in the original group)
    const originalOpeningIds = new Set(payload.openingGroups.originalGroup!.openings.map((opening) => opening.id))

    // Check if it's a move (different groups) or same group update
    const isSameGroup = payload.openingGroups.originalGroup?.id === payload.openingGroups.newGroup.id

    // For moves (different groups), get geometries from original group to update source group state
    const originalGroupGeometries = getOriginalGroupGeometriesForMove(payload.openingGroups)

    // Check if any opening in the active group is moving to a different group
    // If so, update activeGeometryGroupID to the new group
    const activeGroupAffected = newGeometries.some((geo) => {
        const existingGeo =
            state.geometries.find((g) => g.id === geo.id) ??
            state.geometries.find((g) => g.opening_id === geo.opening_id)

        return (
            existingGeo &&
            existingGeo.geometryGroupId === state.activeGeometryGroupID &&
            geo.geometryGroupId !== state.activeGeometryGroupID
        )
    })

    if (activeGroupAffected) {
        state.activeGeometryGroupID = payload.openingGroups.newGroup.id
    }

    // Combine geometries from both newGroup and originalGroup (for moves)
    const allNewGeometries = [...newGeometries, ...originalGroupGeometries]

    // Preserve isActive state from existing geometries and set visibility for new openings
    allNewGeometries.forEach((geo) => {
        const existingGeo =
            state.geometries.find((g) => g.id === geo.id) ??
            state.geometries.find((g) => g.opening_id === geo.opening_id)

        if (existingGeo?.isActive !== undefined) {
            geo.isActive = existingGeo.isActive
        }

        // If this is a newly created opening (not in the original group), ensure it's visible
        // BUT only if it doesn't already have a visibility state (don't overwrite hidden materials)
        if (!originalOpeningIds.has(geo.opening_id) && state.geometryVisibilityRecord[geo.opening_id] === undefined) {
            state.geometryVisibilityRecord[geo.opening_id] = true
        }
    })

    payload.openingGroups.newGroup.openings.forEach((opening) => {
        geoOpeningIdsAlreadyAddedByNewGeometries[opening.id] = true
    })

    // Track openings that need to be removed and re-added with updated data
    allNewGeometries.forEach((g) => openingIdsAffected.add(g.opening_id))

    // For same group (deletion case), also remove openings that were deleted
    if (isSameGroup) {
        const newGroupOpeningIds = new Set(payload.openingGroups.newGroup.openings.map((opening) => opening.id))

        originalOpeningIds.forEach((id) => {
            if (!newGroupOpeningIds.has(id)) {
                openingIdsAffected.add(id)
            }
        })
    } else {
        // For moves, also remove openings from the original group that are no longer there
        // (i.e., the opening that moved out)
        const originalGroupOpeningIds = new Set(
            payload.openingGroups.originalGroup?.openings.map((opening) => opening.id) || []
        )

        originalOpeningIds.forEach((id) => {
            if (!originalGroupOpeningIds.has(id)) {
                openingIdsAffected.add(id)
            }
        })
    }

    // Location IDs survive an opening replacement; remove their previous records as well.
    const updatedLocationIds = new Set(allNewGeometries.map((geo) => geo.id))
    const filteredGeometries = state.geometries.filter(
        (geo) => !openingIdsAffected.has(geo.opening_id) && !updatedLocationIds.has(geo.id)
    )

    state.geometries = filteredGeometries.concat([...allNewGeometries])

    state.allGroupIds = [...new Set(state.geometries.map((geo) => geo.geometryGroupId))]
}

function handleDeleteGeometries(
    state: GeometryState,
    { payload }: PayloadAction<{ openingGroupsToDelete: OpeningGroupAPI[] }>
) {
    const newGeometries: Geometry[] = convertOpeningsAndLocationsToGeometries(payload.openingGroupsToDelete)

    const geometriesIdsToRemove = newGeometries.map((geo) => geo.geometryGroupId)

    state.geometries = state.geometries.filter((geo) => !geometriesIdsToRemove.includes(geo.geometryGroupId))
    state.allGroupIds = [
        ...new Set(
            state.geometries
                .filter((geo) => !geometriesIdsToRemove.includes(geo.geometryGroupId))
                .map((geo) => geo.geometryGroupId)
        ),
    ]
}

function handleUpdateGeometryMeasurements(
    state: GeometryState,
    { payload }: PayloadAction<UpdateDrawableMeasurements>
) {
    state.geometries = state.geometries.map((geo) => {
        const isGeoMatching = geo.id === payload.updatedLocationID || geo.opening_id === payload.activeDrawableId

        if (!isGeoMatching) return geo

        // Update opening settings
        geo.openingSettings = {
            ...geo.openingSettings,
            quantity: payload.openingMeasurements.quantity,
            linear_total: payload.openingMeasurements.linear_total,
        }

        // Update group measurements
        geo.settings.quantity = payload.groupMeasurements.quantity ? Number(payload.groupMeasurements.quantity) : 0
        geo.settings.linear_total = payload.groupMeasurements.linear_total
            ? Number(payload.groupMeasurements.linear_total)
            : 0

        // Update coordinates and cutouts if location ID matches
        if (geo.id === payload.updatedLocationID) {
            geo.coordinates = payload.coordinates
            geo.cutouts = payload.cutouts || geo.cutouts || []
        }

        return geo
    })
}

function handleToggleGeometryGroup(
    state: GeometryState,
    { payload }: PayloadAction<{ drawables: GeometryGroup | []; activeChunkId: number | undefined }>
) {
    if (!isArray(payload.drawables) && payload.activeChunkId) {
        const previousStateExists =
            state.geometryGroupActiveRecord[payload.drawables.id] &&
            state.geometryGroupActiveRecord[payload.drawables.id][payload.activeChunkId] !== undefined

        const newActiveStatus = previousStateExists
            ? !state.geometryGroupActiveRecord[payload.drawables.id][payload.activeChunkId]
            : false

        state.geometryGroupActiveRecord = {
            ...state.geometryGroupActiveRecord,
            [payload.drawables.id]: {
                ...state.geometryGroupActiveRecord[payload.drawables.id],
                [payload.activeChunkId]: newActiveStatus,
            },
        }

        const updatedVisRecords = state.geometries.reduce((record, geo) => {
            if (
                geo.geometryGroupId === (payload.drawables as GeometryGroup).id &&
                geo.document_chunk_id === payload.activeChunkId
            ) {
                record[geo.opening_id] = newActiveStatus
            }

            return record
        }, {} as GeometryVisibilityRecord)

        state.geometryVisibilityRecord = {
            ...state.geometryVisibilityRecord,
            ...updatedVisRecords,
        }
    }
}

function handleToggleGeometryGroupsByType(
    state: GeometryState,
    { payload }: PayloadAction<{ type: GeometryGroup[]; activeChunkId: number | undefined }>
) {
    const docId = payload.activeChunkId

    if (docId) {
        const geometryGroupIds: number[] = payload.type.map((group) => group.id)
        const isAllActive: boolean = geometryGroupIds.every(
            (geoGroupId) =>
                !state.geometryGroupActiveRecord[geoGroupId] ||
                state.geometryGroupActiveRecord[geoGroupId]?.[docId] ||
                isUndefined(state.geometryGroupActiveRecord[geoGroupId]?.[docId])
        )

        geometryGroupIds.forEach((groupID) => {
            state.geometryGroupActiveRecord = {
                ...state.geometryGroupActiveRecord,
                [groupID]: {
                    ...state.geometryGroupActiveRecord[groupID],
                    [docId]: !isAllActive,
                },
            }
        })

        const updatedVisRecords = state.geometries.reduce((record, geo) => {
            // keep openings from active geometry group always visible
            if (state.activeGeometryGroupID === geo.geometryGroupId) {
                record[geo.opening_id] = true

                return record
            }

            if (geometryGroupIds.includes(geo.geometryGroupId) && geo.document_chunk_id === docId) {
                record[geo.opening_id] = !isAllActive
            }

            return record
        }, {} as GeometryVisibilityRecord)

        state.geometryVisibilityRecord = {
            ...state.geometryVisibilityRecord,
            ...updatedVisRecords,
        }
    }
}

function handleActivatingSingleGeometryGroup(
    state: GeometryState,
    { payload }: PayloadAction<{ type: any[]; activeChunkId: number | undefined }>
) {
    const docId = payload.activeChunkId

    if (docId) {
        const geometryGroupIds: number[] = payload.type.map((group) => group.id)
        const allOtherGeometryGroupIds: number[] = state.allGroupIds.filter((id) => !geometryGroupIds.includes(id))

        geometryGroupIds.forEach((groupID) => {
            state.geometryGroupActiveRecord = {
                ...state.geometryGroupActiveRecord,
                [groupID]: {
                    ...state.geometryGroupActiveRecord[groupID],
                    [docId]: true,
                },
            }
        })

        allOtherGeometryGroupIds.forEach((groupId) => {
            state.geometryGroupActiveRecord = {
                ...state.geometryGroupActiveRecord,
                [groupId]: {
                    ...state.geometryGroupActiveRecord[groupId],
                    [docId]: false,
                },
            }
        })

        const updatedVisRecords = state.geometries.reduce((record, geo) => {
            if (geo.document_chunk_id === docId) {
                record[geo.opening_id] = geometryGroupIds.includes(geo.geometryGroupId)
            }

            return record
        }, {} as GeometryVisibilityRecord)

        state.geometryVisibilityRecord = {
            ...state.geometryVisibilityRecord,
            ...updatedVisRecords,
        }
    }
}

function handleResetGeometryGroupVisibility(
    state: GeometryState,
    { payload }: PayloadAction<{ isShowAllTabs: boolean }>
) {
    if (payload.isShowAllTabs) {
        state.geometryGroupActiveRecord = {}
    }
}

function handleToggleActiveGeometries(
    state: GeometryState,
    { payload }: PayloadAction<{ drawable_id: number[] | number }>
) {
    if (!isArray(payload.drawable_id)) {
        state.geometries = state.geometries.map((geo) => {
            if (geo.opening_id === payload.drawable_id) {
                geo.isActive = geo.isActive === undefined || !geo.isActive
            }

            return geo
        })
    } else {
        const geometriesTargeted = [...state.geometries].filter((geo) =>
            (payload.drawable_id as number[]).includes(geo.opening_id)
        )

        if (geometriesTargeted.length === 0) return
        const isAllInActive = geometriesTargeted.every((geo) => geo.isActive === false)

        state.geometries = state.geometries.map((geo) => {
            if ((payload.drawable_id as number[]).includes(geo.opening_id)) {
                return { ...geo, isActive: isAllInActive }
            }

            return geo
        })
    }
}

function handleUpdateGeometryJoistLines(state: GeometryState, { payload }: PayloadAction<{ res: OpeningGroupAPI }>) {
    const activeStateByOpeningId = new Map(state.geometries.map((geo) => [geo.opening_id, geo.isActive]))
    const geometries = convertOpeningsAndLocationsToGeometries([payload.res])

    geometries.forEach((geo) => {
        const isActive = activeStateByOpeningId.get(geo.opening_id)

        if (isActive !== undefined) geo.isActive = isActive
    })

    const openingIds = payload.res.openings.map((opening) => opening.id)
    const filteredGeometries = state.geometries.filter((geo) => !openingIds.includes(geo.opening_id))

    state.geometries = [...filteredGeometries, ...geometries]
}

const extraReducers = (builder: ActionReducerMapBuilder<GeometryState>): void => {
    builder.addCase(gotOpeningGroups, handleGotOpeningGroups)
    builder.addCase(setActiveGroup, handleSetActiveGroup)
    builder.addCase(updateGeometries, handleUpdateGeometries)
    builder.addCase(deleteGeometries, handleDeleteGeometries)
    builder.addCase(updateGeometryMeasurements, handleUpdateGeometryMeasurements)
    builder.addCase(toggleGeometryGroup, handleToggleGeometryGroup)
    builder.addCase(toggleGeometryGroupByType, handleToggleGeometryGroupsByType)
    builder.addCase(activateSingleGeometryGroupByType, handleActivatingSingleGeometryGroup)
    builder.addCase(resetGeometryGroupVisibility, handleResetGeometryGroupVisibility)
    builder.addCase(toggleActiveGeometries, handleToggleActiveGeometries)
    builder.addCase(updateGeometryJoistLines, handleUpdateGeometryJoistLines)
}

const geometrySlice = createSlice({
    name: 'geometry',
    initialState: initialGeometryState,
    reducers: {
        createBlankGroup(state, action: PayloadAction<ActiveGeometryGroup | null>) {
            state.groupToCreate = action.payload
        },
        updateGeometricLocationAdditionalData(
            state,
            action: PayloadAction<{
                locationAdditionalData: IndexableObject
                id: number
                openingAdditionalData?: IndexableObject
            }>
        ) {
            state.geometries = state.geometries.map((geo) => {
                if (geo.id === action.payload.id) {
                    return {
                        ...geo,
                        additional_data: action.payload.locationAdditionalData,
                        openingAdditionalData: action.payload.openingAdditionalData ?? geo.openingAdditionalData,
                    }
                }

                return geo
            })
        },
        updateGeometriesRcmBuildingId(
            state,
            action: PayloadAction<{ rcmBuildingId: string; documentChunkId: number }>
        ) {
            const { rcmBuildingId, documentChunkId } = action.payload

            // update geometry rcm_building id when document chunk was moved to another building
            state.geometries = state.geometries.map((geo) => {
                if (geo.document_chunk_id === documentChunkId) {
                    return {
                        ...geo,
                        openingSettings: {
                            ...geo.openingSettings,
                            rcm_building_id: rcmBuildingId,
                        },
                        settings: {
                            ...geo.settings,
                            rcm_building_id: rcmBuildingId,
                        },
                    }
                }

                return geo
            })
        },
        deleteGeometriesByIds(state, { payload }: PayloadAction<number[]>) {
            const restGeometries = state.geometries.filter((geometry) => !payload.includes(geometry.opening_id))
            const restAllGroupIds = restGeometries.map((geo) => geo.geometryGroupId)

            state.geometries = restGeometries
            state.allGroupIds = restAllGroupIds
        },
        toggleGeometriesVisibility(state, { payload }: PayloadAction<number[]>) {
            const newVisibilityEntries = payload.reduce((newVisibilityEntries, id) => {
                const currentValue = state.geometryVisibilityRecord[id] ?? true

                newVisibilityEntries[id] = !currentValue

                return newVisibilityEntries
            }, {} as Record<number, boolean>)

            state.geometryVisibilityRecord = {
                ...state.geometryVisibilityRecord,
                ...newVisibilityEntries,
            }
        },
        updateGeometryVisibility(state, { payload }: PayloadAction<GeometryVisibilityRecord>) {
            state.geometryVisibilityRecord = {
                ...state.geometryVisibilityRecord,
                ...payload,
            }
        },
    },
    extraReducers,
})

export default geometrySlice

export const {
    createBlankGroup,
    updateGeometricLocationAdditionalData,
    updateGeometriesRcmBuildingId,
    deleteGeometriesByIds,
    toggleGeometriesVisibility,
    updateGeometryVisibility,
} = geometrySlice.actions

/** SELECTORS */
export const selectGeometries = createSelector(
    (state: RootState) => state.IMUP.geometry.geometries,
    (geometries) => geometries
)

export const selectActiveGeometryGroup = createSelector(
    [
        (state: RootState) => state.IMUP.geometry.geometries,
        (state: RootState) => state.IMUP.documents.documentChunks,
        (state: RootState) => selectActiveDocumentChunkIds(state),
        (state: RootState) => state.IMUP.geometry.activeGeometryGroupID,
        (state: RootState) => state.IMUP.geometry.geometryVisibilityRecord,
    ],
    (
        geometries,
        documentChunks,
        activeChunkIds,
        activeGroupId,
        geometryVisibilityRecord
    ): ActiveGeometryGroup | null => {
        try {
            if (isNull(activeGroupId)) {
                return null
            }
            const geometriesInGroup = geometries.filter((geo) => geo.geometryGroupId === activeGroupId)

            if (geometriesInGroup.length === 0) return null

            const { drawables, groupSettings } = convertGeometriesToGeometricDrawable(
                documentChunks,
                geometriesInGroup,
                geometryVisibilityRecord
            )

            if (drawables.length === 0) return null

            return {
                openings: drawables,
                drawables:
                    activeChunkIds.length > 0
                        ? drawables.filter((geoDrawable) =>
                              geoDrawable.opening_locations.find((loc) =>
                                  activeChunkIds.includes(loc.document_chunk_id)
                              )
                          )
                        : drawables,
                id: drawables[0].opening_group_id,
                color: drawables[0].color,
                type: drawables[0].type,
                settings: groupSettings as any,
                remarks: drawables[0].remarks,
                configuration: drawables[0].configuration,
                project_id: drawables[0].project_id,
            }
        } catch (e) {
            console.error(e)

            return null
        }
    }
)

function createHashOfAllGeometriesBasedOnGroupId(geometries: Geometry[]) {
    return geometries.reduce((hash, geo) => {
        if (!hash[geo.geometryGroupId]) {
            hash[geo.geometryGroupId] = []
        }
        hash[geo.geometryGroupId].push(geo)

        return hash
    }, {} as Record<number, Geometry[]>)
}

function generateGroupsBasedOnDrawablesPerTabPredicate({
    allGroupIds,
    geometries,
    documentChunks,
    geometryGroupActiveRecord,
    drawablesPerTabFilterFn,
    groupFilterPredicate,
    geometryVisibilityRecord,
}: {
    allGroupIds: number[]
    geometries: Geometry[]
    documentChunks: NormalizedDocumentChunk[] | null
    geometryGroupActiveRecord: GeometryActiveDictionary
    // Uses Array.filter logic; true to include, false to exclude
    // applies to drawablesPerTabFilterFn and groupFilterPredicate
    drawablesPerTabFilterFn: (drawable: GeometricDrawable) => boolean
    groupFilterPredicate: (drawablesPerTab: GeometricDrawable[]) => boolean
    geometryVisibilityRecord: GeometryVisibilityRecord
}) {
    const geometriesHash = createHashOfAllGeometriesBasedOnGroupId(geometries)

    return allGroupIds.reduce((groups, groupId) => {
        const geometriesInGroup = geometriesHash[groupId]

        const { drawables, groupSettings } = convertGeometriesToGeometricDrawable(
            documentChunks,
            geometriesInGroup,
            geometryVisibilityRecord
        )

        if (drawables.length === 0) return [] as GeometryGroup[]

        const drawablesPerTab = drawables.filter(drawablesPerTabFilterFn)

        if (drawablesPerTab.length > 0 || groupFilterPredicate(drawablesPerTab)) {
            groups.push({
                openings: drawables,
                drawablesPerTab: drawablesPerTab,
                id: drawables[0].opening_group_id,
                color: drawables[0].color,
                type: drawables[0].type,
                settings: groupSettings as any,
                remarks: drawables[0].remarks,
                configuration: drawables[0].configuration,
                project_id: drawables[0].project_id,
                isActive: geometryGroupActiveRecord[groupId] ?? {},
            })
        }

        return groups
    }, [] as GeometryGroup[])
}

export const selectDrawableGroupsGeometries = createSelector(
    [
        (_: RootState, isIgnorePageFilter?: boolean) => isIgnorePageFilter,
        (state: RootState) => state.IMUP.geometry.geometries,
        (state: RootState) => state.IMUP.documents.documentChunks,
        (state: RootState) => state.IMUP.geometry.geometryGroupActiveRecord,
        (state: RootState) => selectActiveDocumentChunkIds(state),
        (state: RootState) => state.IMUP.geometry.allGroupIds,
        (state: RootState) => state.drawable.isShowAllTabs,
        (state: RootState) => state.IMUP.geometry.geometryVisibilityRecord,
    ],
    (
        isIgnorePageFilter,
        allGeometries,
        documentChunks,
        geometryGroupActiveRecord,
        activeChunkIds,
        allGroupIds,
        isShowAllTabs,
        geometryVisibilityRecord
    ) => {
        const drawablesPerTabFilterFn = (drawable: GeometricDrawable) => {
            if (activeChunkIds.length > 0 || !isIgnorePageFilter) {
                return !!drawable.opening_locations.find((loc) => activeChunkIds.includes(loc.document_chunk_id))
            }

            return true
        }

        return generateGroupsBasedOnDrawablesPerTabPredicate({
            allGroupIds,
            geometries: allGeometries,
            documentChunks,
            drawablesPerTabFilterFn,
            groupFilterPredicate: () => isShowAllTabs,
            geometryGroupActiveRecord,
            geometryVisibilityRecord,
        })
    }
)

export const selectDrawableGroupsGeometriesForBaseHome = createSelector(
    [
        (_: RootState, isIgnorePageFilter?: boolean) => isIgnorePageFilter,
        (state: RootState) => state.IMUP.geometry.geometries,
        (state: RootState) => state.IMUP.documents.documentChunks,
        (state: RootState) => state.IMUP.geometry.geometryGroupActiveRecord,
        (state: RootState) => state.IMUP.documents.activeFloorDocId,
        (state: RootState) => state.IMUP.geometry.allGroupIds,
        (state: RootState) => state.drawable.isShowAllTabs,
        (state: RootState) => state.IMUP.geometry.geometryVisibilityRecord,
    ],
    (
        isIgnorePageFilter,
        allGeometries,
        documentChunks,
        geometryGroupActiveRecord,
        activeChunkId,
        allGroupIds,
        isShowAllTabs,
        geometryVisibilityRecord
    ) => {
        const drawablesPerTabFilterFn = (drawable: GeometricDrawable) => {
            if (activeChunkId !== null || !isIgnorePageFilter) {
                return !!drawable.opening_locations.find((loc) => loc.document_chunk_id === activeChunkId)
            }

            return true
        }

        return generateGroupsBasedOnDrawablesPerTabPredicate({
            allGroupIds,
            geometries: allGeometries,
            documentChunks,
            drawablesPerTabFilterFn,
            groupFilterPredicate: () => isShowAllTabs,
            geometryGroupActiveRecord,
            geometryVisibilityRecord,
        })
    }
)

export const selectAllDrawableGroupsGeometries = createSelector(
    [
        (state: RootState) => state.IMUP.geometry.geometries,
        (state: RootState) => state.IMUP.documents.documentChunks,
        (state: RootState) => state.IMUP.geometry.geometryGroupActiveRecord,
        (state: RootState) => state.IMUP.geometry.allGroupIds,
        (state: RootState) => state.IMUP.geometry.geometryVisibilityRecord,
    ],
    (allGeometries, documentChunks, geometryGroupActiveRecord, allGroupIds, geometryVisibilityRecord) => {
        const drawablesPerTabFilterFn = () => true

        return generateGroupsBasedOnDrawablesPerTabPredicate({
            allGroupIds,
            geometries: allGeometries,
            documentChunks,
            drawablesPerTabFilterFn,
            groupFilterPredicate: () => true,
            geometryGroupActiveRecord,
            geometryVisibilityRecord,
        })
    }
)

export const selectDrawableGroupsGeometriesHash = createSelector(
    [
        (_: RootState, shouldIgnorePagesFilter?: boolean) => shouldIgnorePagesFilter,
        (state: RootState) => state.IMUP.geometry.geometries,
        (state: RootState) => state.IMUP.documents.documentChunks,
        (state: RootState) => state.IMUP.geometry.geometryGroupActiveRecord,
        (state: RootState) => selectActiveDocumentChunkIds(state),
        (state: RootState) => state.IMUP.geometry.allGroupIds,
        (state: RootState) => state.drawable.isShowAllTabs,
        (state: RootState) => state.IMUP.geometry.geometryVisibilityRecord,
    ],
    (
        shouldIgnorePagesFilter,
        allGeometries,
        documentChunks,
        geometryGroupActiveRecord,
        activeChunkIds,
        allGroupIds,
        isShowAllTabs,
        geometryVisibilityRecord
    ) => {
        const geometriesHash = createHashOfAllGeometriesBasedOnGroupId(allGeometries)

        try {
            return allGroupIds.reduce((groups, groupId) => {
                const geometriesInGroup = geometriesHash[groupId]

                const { drawables, groupSettings } = convertGeometriesToGeometricDrawable(
                    documentChunks,
                    geometriesInGroup,
                    geometryVisibilityRecord
                )

                if (drawables.length === 0) return [] as GeometryGroup[]

                const drawablesPerTab =
                    activeChunkIds.length > 0
                        ? drawables.filter((geoDrawable) =>
                              geoDrawable.opening_locations.find((loc) =>
                                  activeChunkIds.includes(loc.document_chunk_id)
                              )
                          )
                        : drawables

                if (isShowAllTabs || drawablesPerTab.length > 0 || shouldIgnorePagesFilter) {
                    groups[drawables[0].opening_group_id] = {
                        openings: drawables,
                        drawablesPerTab: drawablesPerTab,
                        id: drawables[0].opening_group_id,
                        color: drawables[0].color,
                        type: drawables[0].type,
                        settings: groupSettings as any,
                        remarks: drawables[0].remarks,
                        configuration: drawables[0].configuration,
                        project_id: drawables[0].project_id,
                        isActive: geometryGroupActiveRecord[groupId] ?? {},
                    }
                }

                return groups
            }, {} as Record<number, GeometryGroup>)
        } catch (e) {
            console.error(e)

            return {}
        }
    }
)

export const selectDrawableGeometries = createSelector(
    [
        (state: RootState) => state.IMUP.geometry.geometries,
        (state: RootState) => state.IMUP.documents.documentChunks,
        (state: RootState) => state.IMUP.geometry.geometryGroupActiveRecord,
        (state: RootState) => selectActiveDocumentChunkIds(state),
        (state: RootState) => state.IMUP.navigation.activeTab,
        (state: RootState) => state.IMUP.geometry.geometryVisibilityRecord,
        (_: RootState, ignoreHidden = false) => ignoreHidden,
    ],
    (
        allGeometries,
        documentChunks,
        geometryGroupActiveRecord,
        activeChunkIds,
        activeTab,
        geometryVisibilityRecord,
        ignoreHidden
    ) => {
        try {
            const groupActiveRecord = geometryGroupActiveRecord
            const { drawables } = convertGeometriesToGeometricDrawable(
                documentChunks,
                allGeometries,
                geometryVisibilityRecord
            )

            if (activeChunkIds.length === 0 || activeTab === PROJECT_PAGE_TAB_VALUES.PLANS) {
                return drawables
            }

            // get drawables even if there no active drawable group or it's hidden
            if (ignoreHidden) {
                return drawables.filter((geoDrawable) =>
                    activeChunkIds.includes(geoDrawable.opening_locations[0].document_chunk_id)
                )
            }

            return drawables.filter((geoDrawable) => {
                const drawableChunkId = geoDrawable.opening_locations[0].document_chunk_id
                const isDrawableOnPage = activeChunkIds.includes(drawableChunkId)

                const isGroupNotInRecord =
                    isUndefined(groupActiveRecord[geoDrawable.opening_group_id]) ||
                    isUndefined(groupActiveRecord[geoDrawable.opening_group_id][drawableChunkId])
                const isGroupActive =
                    !isGroupNotInRecord && groupActiveRecord[geoDrawable.opening_group_id][drawableChunkId]

                return isDrawableOnPage && (isGroupNotInRecord || isGroupActive)
            })
        } catch (e) {
            console.error(e)

            return []
        }
    }
)

export const selectAllGeometricDrawables = createSelector(
    [
        (state: RootState) => state.IMUP.geometry.geometries,
        (state: RootState) => state.IMUP.documents.documentChunks,
        (state: RootState) => state.IMUP.geometry.geometryVisibilityRecord,
    ],
    (allGeometries, documentChunks, geometryVisibilityRecord) => {
        try {
            const { drawables } = convertGeometriesToGeometricDrawable(
                documentChunks,
                allGeometries,
                geometryVisibilityRecord
            )

            return drawables
        } catch (e) {
            console.error(e)

            return [] as GeometricDrawable[]
        }
    }
)

export const selectActiveGroupToCreate = createSelector(
    (state: RootState) => {
        return state.IMUP.geometry.groupToCreate
    },
    (group) => group
)

export const selectActiveGeometryGroupId = createSelector(
    (state: RootState) => state.IMUP.geometry.activeGeometryGroupID,
    (id) => id
)

export const selectGeometryVisibilityRecord = createSelector(
    (state: RootState) => state.IMUP.geometry.geometryVisibilityRecord,
    (record) => record
)

export const selectHasGeometriesInRegion = createSelector(
    [
        (state: RootState) => state.IMUP.geometry.geometries,
        (state: RootState) => selectActiveDocumentChunkIds(state),
        (_: RootState, regionId?: number, regionCoordinates?: [number, number][]) => ({ regionId, regionCoordinates }),
    ],
    (geometries, activeDocumentChunkIds, { regionId, regionCoordinates }): boolean => {
        // If both region ID and coordinates are not provided or there's no active document chunk,
        // return false (no need to poll)
        if ((!regionId && (!regionCoordinates || regionCoordinates.length === 0)) || !activeDocumentChunkIds.length) {
            return false
        }

        return geometries
            .filter((geo) => activeDocumentChunkIds.includes(geo.document_chunk_id))
            .some((geo) => {
                // Check if geometry is explicitly assigned to this region
                const hasRegionId = regionId != null && geo.region_id === regionId

                // Check if geometry coordinates are spatially inside the region
                const hasCoordinatesInRegion =
                    regionCoordinates && regionCoordinates.length > 0 && hasGeometryInRegion(geo, regionCoordinates)

                // Return true if either condition is met
                return hasRegionId || hasCoordinatesInRegion
            })
    }
)
